import { Check, Gamepad2, RefreshCw, Trophy, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FIBRE_COLOURS,
  describeSubnet,
  dnsDrill,
  generalDrill,
  intToIp,
  locateStrand,
  opticalDrill,
  ponDrill,
  prefixForHosts,
} from '@jt/domain';
import { PageNav } from '../components/PageNav';
import { useSession } from '../lib/session';

/**
 * Drills — small technical games, for the trade this business is in.
 *
 * Two of them, and both are things a GoSpeedNet technician does for real:
 * sizing a subnet for a new block of flats, and finding strand 87 of a 144
 * before cutting anything. Getting either wrong on site costs an afternoon,
 * and both are exactly the kind of thing that goes rusty between jobs.
 *
 * **No external API, deliberately.** A trivia service would mean a key to keep
 * out of the repository, a hole in the content-security-policy, a bill, and
 * questions about film stars rather than fibre. The questions are generated
 * here from `@jt/domain`, which means they work in a van with no signal, cost
 * nothing, and are marked by the same arithmetic the rest of the system uses —
 * so a drill cannot teach something the application would contradict.
 *
 * Scores are per-session and go nowhere. Nobody's performance review should
 * ever mention a game.
 */

type Question = { prompt: string; hint?: string; answer: string; accepts: (given: string) => boolean };

/** Crypto-grade randomness is pointless here; readable is the priority. */
const pick = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)]!;
const between = (low: number, high: number): number =>
  low + Math.floor(Math.random() * (high - low + 1));

const normalise = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '');

/* ── Subnetting ─────────────────────────────────────────────────────────── */

function subnetQuestion(): Question {
  const base = pick([
    `10.${between(0, 60)}.${between(0, 255)}.${between(1, 254)}`,
    `192.168.${between(0, 30)}.${between(1, 254)}`,
    `172.${between(16, 31)}.${between(0, 255)}.${between(1, 254)}`,
  ]);
  const prefix = pick([22, 23, 24, 25, 26, 27, 28, 29, 30, 31]);
  const net = describeSubnet(base, prefix)!;

  const kind = pick(['network', 'broadcast', 'hosts', 'first', 'size'] as const);

  if (kind === 'hosts') {
    return {
      prompt: `How many usable addresses in ${base}/${prefix}?`,
      hint: prefix >= 31 ? 'Careful — this one is not the usual rule.' : undefined,
      answer: String(net.usableHosts),
      accepts: (given) => normalise(given) === String(net.usableHosts),
    };
  }

  if (kind === 'size') {
    const need = pick([2, 6, 12, 25, 60, 120, 250, 500]);
    const answer = prefixForHosts(need)!;
    return {
      prompt: `A block of flats needs ${need} addresses. What is the smallest prefix that fits?`,
      hint: 'Answer as /nn.',
      answer: `/${answer}`,
      accepts: (given) => normalise(given).replace('/', '') === String(answer),
    };
  }

  const wanted =
    kind === 'network' ? net.network : kind === 'broadcast' ? net.broadcast : net.firstHost;

  const label =
    kind === 'network' ? 'network address' : kind === 'broadcast' ? 'broadcast address' : 'first usable address';

  return {
    prompt: `What is the ${label} of ${base}/${prefix}?`,
    hint: prefix >= 31 ? 'A /31 and a /32 do not work like the rest.' : undefined,
    answer: wanted ?? 'none',
    accepts: (given) => normalise(given) === normalise(wanted ?? 'none'),
  };
}

/* ── Fibre ──────────────────────────────────────────────────────────────── */

function fibreQuestion(): Question {
  const kind = pick(['strand', 'position', 'locate'] as const);

  if (kind === 'strand') {
    const strand = between(1, 12);
    const colour = FIBRE_COLOURS[strand - 1]!;
    return {
      prompt: `In a 12-fibre tube, what colour is strand ${strand}?`,
      answer: colour,
      accepts: (given) => normalise(given) === normalise(colour),
    };
  }

  if (kind === 'position') {
    const colour = pick(FIBRE_COLOURS);
    const position = FIBRE_COLOURS.indexOf(colour) + 1;
    return {
      prompt: `Which strand number is ${colour}?`,
      answer: String(position),
      accepts: (given) => normalise(given).replace(/\D/g, '') === String(position),
    };
  }

  const absolute = between(1, 144);
  const found = locateStrand(absolute)!;
  return {
    prompt: `In a 144-count cable, which tube holds strand ${absolute}?`,
    hint: 'Twelve strands to a tube. Answer with the tube colour.',
    answer: found.tubeColour,
    accepts: (given) => normalise(given) === normalise(found.tubeColour),
  };
}

/**
 * Six banks now, and every one of them is something a technician here does for
 * real. The four newest live in `@jt/domain` so they can be tested — a drill
 * that marks its own stated answer wrong teaches the wrong thing, and there is
 * nobody to appeal to.
 */
const GAMES = {
  subnet: { title: 'Subnetting', blurb: 'Sizing a block, and finding its edges.', make: subnetQuestion },
  fibre: { title: 'Fibre order', blurb: 'TIA-598 — the order at both ends.', make: fibreQuestion },
  optical: {
    title: 'Light budget',
    blurb: 'Will the link come up, or come up until it rains?',
    make: opticalDrill,
  },
  pon: { title: 'PON planning', blurb: 'Splitters, ports and how many homes.', make: ponDrill },
  dns: { title: 'DNS & DHCP', blurb: 'The calls that are not a fibre fault.', make: dnsDrill },
  general: { title: 'Networking', blurb: 'General trade knowledge.', make: generalDrill },
} as const;

type GameId = keyof typeof GAMES;

export function DrillsPage() {
  const { drillsVisible } = useSession();

  /*
   * Asked here as well as in the rail, because hiding a menu entry is not an
   * answer to somebody who has the address — a bookmark, a link a colleague
   * sent, the back button. A page that renders anyway makes the toggle look
   * broken, which is the state this whole thing was in.
   *
   * Not a boundary, and it does not pretend to be: the questions are generated
   * on the client from `@jt/domain` and are already in the bundle the browser
   * downloaded. That is fine for what this is. Switching drills off keeps them
   * out of somebody's way, not out of their hands, and there is nothing here
   * worth keeping out of anybody's hands — no business data, no score, nothing
   * recorded.
   */
  if (!drillsVisible) return <SwitchedOff />;

  return <Drills />;
}

/** What somebody sees when an admin has switched drills off for their role. */
function SwitchedOff() {
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageNav crumbs={[{ label: 'Drills' }]} />
      <div className="card text-center">
        <Gamepad2 className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="mt-3 font-heading text-xl">Drills are switched off for your role</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          They are practice quizzes — subnetting, fibre colours, optical budgets — and an
          administrator has chosen not to show them to your role. Nothing about your work is
          affected, and no score was ever recorded. Ask an administrator if you would like them
          back.
        </p>
      </div>
    </div>
  );
}

function Drills() {
  const [game, setGame] = useState<GameId>('subnet');
  const [question, setQuestion] = useState<Question>(() => GAMES.subnet.make());
  const [given, setGiven] = useState('');
  const [verdict, setVerdict] = useState<'right' | 'wrong' | null>(null);
  const [score, setScore] = useState({ right: 0, asked: 0 });
  const [streak, setStreak] = useState(0);

  const next = useCallback(
    (id: GameId = game) => {
      setQuestion(GAMES[id].make());
      setGiven('');
      setVerdict(null);
    },
    [game],
  );

  /**
   * Changing game starts a fresh round.
   *
   * Written without calling `next` so the only dependency is `game` itself —
   * depending on the callback would reset the score every time it was
   * recreated, which is mid-round.
   */
  useEffect(() => {
    setQuestion(GAMES[game].make());
    setGiven('');
    setVerdict(null);
    setScore({ right: 0, asked: 0 });
    setStreak(0);
  }, [game]);

  const submit = () => {
    if (verdict) return next();
    if (!given.trim()) return;

    const right = question.accepts(given);
    setVerdict(right ? 'right' : 'wrong');
    setScore((s) => ({ right: s.right + (right ? 1 : 0), asked: s.asked + 1 }));
    setStreak((s) => (right ? s + 1 : 0));
  };

  const percent = score.asked ? Math.round((score.right / score.asked) * 100) : null;

  const colourSwatch = useMemo(
    () => (game === 'fibre' ? FIBRE_COLOURS : []),
    [game],
  );

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageNav crumbs={[{ label: 'Drills' }]} />

      <div>
        <h2 className="font-heading text-2xl">Drills</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Short technical practice for the work this business actually does. Scores stay on this
          screen and are never recorded against anybody.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(GAMES) as GameId[]).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setGame(id)}
            aria-pressed={game === id}
            className={`flex-1 rounded-xl border px-4 py-3 text-left transition-colors ${
              game === id
                ? 'border-primary-500 bg-primary-50'
                : 'border-border hover:bg-muted'
            }`}
          >
            <span className="block font-semibold">{GAMES[id].title}</span>
            <span className="block text-xs text-muted-foreground">{GAMES[id].blurb}</span>
          </button>
        ))}
      </div>

      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Trophy className="h-4 w-4" aria-hidden="true" />
            {score.asked === 0
              ? 'No answers yet'
              : `${score.right} of ${score.asked} · ${percent}%`}
            {streak >= 3 && (
              <span className="badge bg-primary-50 text-primary-700">{streak} in a row</span>
            )}
          </span>
          <button type="button" className="btn-secondary text-sm" onClick={() => next()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Skip
          </button>
        </div>

        <p className="mt-4 font-heading text-lg">{question.prompt}</p>
        {question.hint && !verdict && (
          <p className="mt-1 text-sm text-muted-foreground">{question.hint}</p>
        )}

        <form
          className="mt-4 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <input
            className="input-field flex-1"
            value={given}
            onChange={(event) => setGiven(event.target.value)}
            placeholder={game === 'fibre' ? 'A colour, or a number' : 'e.g. 10.0.4.0 or 254'}
            autoComplete="off"
            spellCheck={false}
            // Re-enabled after an answer so Enter moves on rather than resubmitting.
            readOnly={verdict !== null}
          />
          <button type="submit" className="btn-primary shrink-0">
            {verdict ? 'Next' : 'Check'}
          </button>
        </form>

        {verdict === 'right' && (
          <p className="mt-3 flex items-center gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700">
            <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
            Correct.
          </p>
        )}

        {verdict === 'wrong' && (
          <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-50 p-3 text-sm text-accent-700">
            <X className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Not quite — it is <strong>{question.answer}</strong>.
            </span>
          </p>
        )}

        {/*
          Why, not just what. Shown after either verdict, because being right
          for the wrong reason is the thing that catches somebody out on site.
          Only the newer banks carry it; the older two are self-evident.
        */}
        {verdict !== null && 'because' in question && (
          <p className="mt-2 rounded-xl bg-muted p-3 text-sm text-muted-foreground">
            {(question as { because: string }).because}
          </p>
        )}
      </div>

      {colourSwatch.length > 0 && (
        <div className="card">
          <h3 className="font-heading text-base">The order, when you need it</h3>
          <ol className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            {colourSwatch.map((colour, index) => (
              <li key={colour} className="flex items-baseline gap-2">
                <span className="w-5 text-right tabular-nums text-muted-foreground">
                  {index + 1}
                </span>
                {colour}
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            Same order for the tubes as for the strands inside them, which is what makes strand 87
            findable without counting to eighty-seven.
          </p>
        </div>
      )}

      {game === 'subnet' && (
        <div className="card">
          <h3 className="font-heading text-base">The two that catch people out</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            A <strong>/31</strong> has two usable addresses, not none — both ends of a
            point-to-point link. A <strong>/32</strong> has one. Every other prefix loses two to the
            network and broadcast addresses.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Worked example: <code>10.0.5.130/22</code> sits in <code>{describeSubnet('10.0.5.130', 22)!.network}</code>{' '}
            through <code>{describeSubnet('10.0.5.130', 22)!.broadcast}</code>, giving{' '}
            {describeSubnet('10.0.5.130', 22)!.usableHosts} usable — starting at{' '}
            <code>{intToIp(0)}</code>-style counting from the network address, not from the address
            you were given.
          </p>
        </div>
      )}
    </div>
  );
}
